import React from 'react';
import { motion } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';

/**
 * A wrapper component that animates its children when they come into view while scrolling
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to be animated
 * @param {number} props.delay - Delay in seconds before animation starts (default: 0)
 * @param {string} props.direction - Direction from which the element appears ('up', 'down', 'left', 'right', default: 'up')
 * @param {number} props.distance - How far the element moves during animation in pixels (default: 50)
 * @param {number} props.duration - Animation duration in seconds (default: 0.5)
 * @param {number} props.threshold - Visibility threshold to trigger animation (0-1, default: 0.1)
 * @param {string} props.type - Animation type ('fade', 'slide', 'both', default: 'both')
 * @returns {JSX.Element}
 */
const ScrollAnimationWrapper = ({
  children,
  delay = 0,
  direction = 'up',
  distance = 50,
  duration = 0.5,
  threshold = 0.1,
  type = 'both',
  ...props
}) => {
  // Use our custom hook to detect when this element is visible
  const [ref, isVisible] = useScrollAnimation({ threshold });

  // Define the initial and animated states based on direction
  const getInitialPosition = () => {
    switch (direction) {
      case 'up': return { y: distance };
      case 'down': return { y: -distance };
      case 'left': return { x: distance };
      case 'right': return { x: -distance };
      default: return { y: distance };
    }
  };

  // Combine opacity and position animations based on the type
  const getInitialState = () => {
    const position = getInitialPosition();
    
    switch (type) {
      case 'fade': return { opacity: 0 };
      case 'slide': return position;
      case 'both':
      default:
        return { opacity: 0, ...position };
    }
  };

  // Animation variants
  const variants = {
    hidden: getInitialState(),
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: duration,
        delay: delay,
        ease: [0.25, 0.1, 0.25, 1.0], // Smooth easing curve
      },
    },
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={variants}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export default ScrollAnimationWrapper; 